<template>
    <div>
        <v-card class="overflow-hidden">
            <v-navigation-drawer
                v-model="showErwSuche"
                absolute
                bottom
                temporary
                right
                width="530px"
                color="kp-light-panel-bg"
            >
                <v-toolbar dense class="kp-sublime-toolbar kp-root-panel-bg" elevation="0">
                    <v-toolbar-title>{{ $t('erw_suche') }}</v-toolbar-title>
                    <v-spacer />
                    <IconBtn icon-cls="x" icon-color="grey" @click="showErwSuche = false" />
                </v-toolbar>
                <AddressEnhancedSearch
                    :initial-params="extendedSearchParams"
                    @search="onEnhancedSearch"
                    @reset="resetEnhancedSearch"
                />
            </v-navigation-drawer>
            <BaseRemoteList
                ref="addressList"
                v-model="selectedItems"
                class="address-list"
                :initial-query="initialQuery"
                :session-key="`address-all-list`"
                :subtitle="$t('adresseintraege')"
                :collection-class="AddressCollection"
                :headers="headers"
                :filters="filters"
                :extra-params="extraParams"
                :options="{ sortBy: ['title'], sortDesc: [false] }"
                :item-class="() => 'address-list-row'"
                cache-data
                show-select
                xlsx-export
                :show-select-all="false"
                height="var(--usable-height-with-double-toolbar)"
                :new-btn="false"
                @loading="loading = $event"
                @edit-item="onEdit"
                @dblclick:row="onRowDblClick"
                @item-selected="onItemSelected"
                @loaded="onLoaded"
            >
                <template #toolbar-start>
                    <v-menu offset-y>
                        <template #activator="{ on, attrs }">
                            <v-btn color="primary white--text" depressed v-bind="attrs" v-on="on">
                                <KpIcon icon-cls="new" color="white" disabled-color="grey" />
                                {{ $t('new_address') }}
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item v-if="userIsStaff()" @click="onNew('event_organizer')">
                                <v-list-item-title>{{ $t('event_organizer') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="onNew('location')">
                                <v-list-item-title>{{ $t('location') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="onNew('person')">
                                <v-list-item-title>{{ $t('person') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="onNew('organization')">
                                <v-list-item-title>{{ $t('organization') }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>

                    <span>|</span>
                </template>
                <template #toolbar-items>
                    <IconBtn
                        color="primary"
                        :disabled="loading"
                        icon-cls="delete"
                        icon-color="white"
                        icon-disabled-color="white"
                        :tooltip="$t('soft_delete')"
                        @click="showSoftDeleteDialog"
                    />
                    <div v-if="userIsStaff()" class="d-flex align-center">
                        |
                        <v-btn-toggle v-model="webformFilter" borderless tile background-color="primary">
                            <IconBtn
                                value="btn_reg"
                                color="primary"
                                :disabled="loading"
                                icon-cls="forms"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('from_webform')"
                            />
                        </v-btn-toggle>
                    </div>

                    |
                    <v-btn-toggle v-model="statusFilter" borderless tile background-color="primary">
                        <IconBtn
                            value="unsigned"
                            color="primary"
                            :disabled="loading"
                            icon-cls="pencil"
                            icon-color="white"
                            icon-disabled-color="white"
                            :tooltip="$t('neu')"
                        />
                        <IconBtn
                            value="requested"
                            color="primary"
                            :disabled="loading"
                            icon-cls="hourglass"
                            icon-color="white"
                            icon-disabled-color="white"
                            :tooltip="$t('registered')"
                        />
                        <IconBtn
                            value="rejected"
                            color="primary"
                            :disabled="loading"
                            icon-cls="rejected"
                            icon-color="white"
                            icon-disabled-color="white"
                            :tooltip="$t('rejected')"
                        />
                        <IconBtn
                            value="in_union"
                            color="primary"
                            :disabled="loading"
                            icon-cls="world"
                            icon-color="white"
                            icon-disabled-color="white"
                            :tooltip="$t('kulturplatz')"
                        />
                    </v-btn-toggle>
                    <div v-if="userIsStaff()" class="d-flex align-center">
                        |
                        <v-btn-toggle v-model="originFilter" borderless tile background-color="primary">
                            <IconBtn
                                value="own"
                                color="primary"
                                :disabled="loading"
                                icon-cls="ownnotimported"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('own_events')"
                            />
                            <IconBtn
                                value="guidle"
                                color="primary"
                                :disabled="loading"
                                icon-cls="guidleimport"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('from_guidle')"
                            />
                            <IconBtn
                                value="eventfrog"
                                color="primary"
                                :disabled="loading"
                                icon-cls="eventfrog"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('from_eventfrog')"
                            />
                        </v-btn-toggle>
                    </div>
                    |
                    <v-btn-toggle v-model="selectedItemsFilter" borderless tile background-color="primary">
                        <IconBtn
                            value="selectedItems"
                            color="primary"
                            :disabled="loading"
                            icon-cls="markedevents"
                            icon-color="white"
                            icon-disabled-color="white"
                            :tooltip="$t('markierte_beitraege')"
                        />
                    </v-btn-toggle>
                    <IconBtn
                        value="selectedItems"
                        color="primary"
                        :disabled="loading"
                        icon-cls="cancelmarked"
                        icon-color="white"
                        icon-disabled-color="white"
                        :tooltip="$t('markierungen_loeschen')"
                        @click="removeSelections"
                    />
                </template>

                <template v-if="userIsStaff()" #after-query-field>
                    <div
                        :class="{
                            'ml-1': true,
                            'erw-suche-indicator': showEnhancedSearchIndicator,
                        }"
                    >
                        <IconBtn
                            color="primary"
                            :disabled="loading"
                            icon-cls="advancedsearch"
                            icon-color="white"
                            icon-disabled-color="white"
                            :tooltip="$t('erw_suche')"
                            @click="showErwSuche = true"
                        />
                    </div>
                </template>

                <template #[`item.context_menu`]="{ item }">
                    <v-menu offset-y>
                        <template #activator="{ on, attrs }">
                            <v-btn dark icon v-bind="attrs" v-on="on">
                                <KpIcon icon-cls="moredots" color="grey" />
                            </v-btn>
                        </template>

                        <AddressContextMenu
                            :record="item"
                            @delete-record="(record) => $refs.addressList.initiateDelete(record)"
                            @duplicate-record="duplicateAddress($event)"
                        />
                    </v-menu>
                </template>

                <template #[`item.image`]="{ item }">
                    <ListImageWithPrevLinks :address-record="item" />
                </template>
                <template #[`item.title`]="{ item }">
                    <list-item-title :item="item" />
                </template>
                <template #[`item.modified`]="{ item }">
                    <TimeDisplay :date="item.modified" />
                </template>
                <template #[`item.address`]="{ item }">
                    <address>
                        {{ item.address }}<br />
                        {{ item.plz }} {{ item.city }}<br />
                    </address>
                </template>
                <template #[`item.contact`]="{ item }">
                    <div>
                        <a v-if="item.tel" :href="`tel:${item.tel}`">{{ item.tel }}</a>
                    </div>
                    <div>
                        <a v-if="item.email" :href="`mailto:${item.email}`">{{ item.email }}</a>
                    </div>
                </template>
                <template #[`item.categories`]="{ item }">
                    <div>
                        {{ item.categories_texts }}
                    </div>
                </template>
            </BaseRemoteList>
        </v-card>
        <AddressNewRecordDialog
            v-if="showNewRecordDialog"
            :value="showNewRecordDialog"
            @input="showNewRecordDialog = $event"
            @new-address="showAddress"
        />
        <v-dialog v-model="isSoftDeleteDialogVisible" scrollable persistent max-width="500px">
            <v-card>
                <v-card-title class="text-h5">{{ $t('delete_selected_dialog_title') }}</v-card-title>
                <v-card-text style="max-height: 300px">
                    <v-row v-for="item in allSelectedItems" :key="item.id">
                        <v-col>{{ item.id }}</v-col>
                        <v-col>{{ item.displayTitle }}</v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="hideSoftDeleteDialog"> {{ $t('disagree') }} </v-btn>
                    <v-btn color="secondary" text @click="agreeDeletion"> {{ $t('agree') }} </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { errToStr } from '@/helpers/tools';
import { AddressCollection } from '@/models/Address';
import { instance as api } from '@/helpers/api';
import sessionStorageMixin from '@/mixins/session-storage';
import ListItemTitle from '@/components/address/list-item-title.vue';
import ListImageWithPrevLinks from '@/components/address/ListImageWithPrevLinks.vue';
import AddressEnhancedSearch from '@/components/address/EnhancedSearch.vue';
import AddressContextMenu from '@/components/address/AddressContextMenu.vue';
import { userIsStaff } from '@/helpers/auth';
import AddressNewRecordDialog from '@/components/address/NewRecordDialog.vue';

export default {
    name: 'AddressList',

    components: {
        ListItemTitle,
        AddressEnhancedSearch,
        ListImageWithPrevLinks,
        AddressContextMenu,
        AddressNewRecordDialog,
    },
    mixins: [sessionStorageMixin],

    props: {
        scope: {
            // one of the supported scopes:
            // all, people, organizations, kulturplatz
            type: String,
            required: true,
        },
    },

    data() {
        this.sessionKey = `address-all-index`;
        // Initial values for filtering: Loaded from the Session storage,
        // OR overridden from route params:
        // The route params allow certain pre-set filters when opening this list.
        let searchFilters = {
            initialQuery: null, // initial query field, only set when given as intial route param filter
            selected: this.getSessionValue('selected') || [], // global selected records
            selectedItemsFilter: this.getSessionValue('selectedItemsFilter') || null, // global selected records
            statusFilter: this.getSessionValue('statusFilter') || null,
            webformFilter: this.getSessionValue('webformFilter') || null,
            originFilter: this.getSessionValue('originFilter') || null,

            extendedSearchParams: this.getSessionValue('extendedSearchParams') || {},

            // will be set when extended search btn is clicked --> initially taken from extended SearchParams
            enhancedFilters: this.getSessionValue('extendedSearchParams') || null,
        };
        if (this.$route.params?.filters) {
            searchFilters = Object.assign(
                searchFilters,
                {
                    initialQuery: '',
                    statusFilter: null,
                    webformFilter: null,
                    originFilter: null,
                    selected: [],
                    selectedItemsFilter: null,
                    extendedSearchParams: {},
                    enhancedFilters: null,
                },
                this.$route.params.filters,
            );
            this.setSessionValue('statusFilter', searchFilters.statusFilter);
            this.setSessionValue('webformFilter', searchFilters.webformFilter);
            this.setSessionValue('originFilter', searchFilters.origin);
            this.setSessionValue('selectedItemsFilter', searchFilters.selectedItemsFilter);
            this.setSessionValue('selected', searchFilters.selected);
            this.setSessionValue('extendedSearchParams', searchFilters.extendedSearchParams);
        }
        const data = Object.assign(
            {
                loading: false,
                AddressCollection,
                headers: [
                    { width: 35, sortable: false, value: 'context_menu' },
                    { text: 'Bild', value: 'image', width: 100, sortable: false },
                    { text: this.$t('titel'), sortable: true, value: 'title' },
                    { text: this.$t('adresse'), value: 'address', sortable: false },
                    { text: this.$t('modified'), value: 'modified', sortable: true, width: 120 },
                    { text: this.$t('kategorien'), value: 'categories', sortable: false },
                    { text: this.$t('tags'), value: 'tag_names', sortable: false, width: 150 },
                    { text: this.$t('contact'), value: 'contact', sortable: false },
                ],
                selectedItems: [], // selected records on current page
                allSelectedItems: [],
                showErwSuche: false,
                showNewRecordDialog: false,
                isSoftDeleteDialogVisible: false,
            },
            // apply search filters as data props
            searchFilters,
        );

        return data;
    },
    computed: {
        filters() {
            const filters = [];
            if (this.selectedItemsFilter) {
                filters.push({
                    property: 'id',
                    value: [...(this.selected.length ? this.selected : [-1])],
                    // TODO: When rewrite Filter Handling: operator: 'IN',
                });
            }
            if (this.statusFilter) {
                filters.push({ property: 'status', value: this.statusFilter });
            }
            switch (this.scope) {
                case 'people':
                    filters.push({ property: 'legal_type', value: 'person' });
                    break;
                case 'organizations':
                    filters.push({ property: 'legal_type', value: 'organization' });
                    break;
                case 'kulturplatz':
                    filters.push({ property: 'status', value: 'in_union' });
                    break;
                case 'locations':
                    filters.push({ property: 'type', value: 'location' });
                    break;
                case 'event_organizers':
                    filters.push({ property: 'type', value: 'event_organizer' });
                    break;
            }
            return filters;
        },

        extraParams() {
            const extraParams = {
                // EO users should only see its own addresses in lists:
                filter_locations_for_eo: true,
            };
            const enhancedFilters = {};

            if (!this.selectedItemsFilter) {
                for (let key in this.enhancedFilters) {
                    if (this.enhancedFilters[key]) {
                        enhancedFilters[key] = this.enhancedFilters[key];
                    }
                }
                if (this.webformFilter) {
                    extraParams[this.webformFilter] = true;
                }
                if (this.originFilter) {
                    extraParams['origin'] = this.originFilter;
                }
                extraParams.enhancedFilters = enhancedFilters;
            }

            return extraParams;
        },

        showEnhancedSearchIndicator() {
            let show = false;
            for (const key in this.enhancedFilters || {}) {
                if (!Array.isArray(this.enhancedFilters[key]) || this.enhancedFilters[key].length > 0) {
                    show = true;
                }
            }
            return show;
        },
    },

    watch: {
        statusFilter(val) {
            this.setSessionValue('statusFilter', val);
        },
        webformFilter(val) {
            this.setSessionValue('webformFilter', val);
        },
        originFilter(val) {
            this.setSessionValue('originFilter', val);
        },
        selectedItemsFilter(val) {
            this.setSessionValue('selectedItemsFilter', val);
        },
    },
    methods: {
        userIsStaff,
        onEdit(record) {
            this.openEntry(record);
        },

        onRowDblClick(e, data) {
            if (data.item) {
                this.openEntry(data.item);
            }
        },

        onItemSelected({ item, value }) {
            const newIds = [...this.selected];
            if (value === true) {
                newIds.push(item.id);
            } else {
                delete newIds[newIds.indexOf(item.id)];
            }

            // unique and filter nulls
            this.selected = [...new Set(newIds.filter(Boolean))];
            this.setSessionValue('selected', this.selected);
        },
        onLoaded(collection) {
            // re-select the selected items from the session id array:
            let selectedItems = [];
            if (this.selected.length) {
                selectedItems = collection.models.filter((item) => this.selected.indexOf(item.id) > -1);
            }
            this.selectedItems = selectedItems;
        },

        removeSelections() {
            this.selected = [];
            this.selectedItemsFilter = null;
            this.setSessionValue('selected', this.selected);
        },

        openEntry(record) {
            // On existing records, we route to the info-category panel directly:
            this.$router.push({ name: 'address-edit', params: { id: record.id, backRoute: { route: this.$route } } });
        },

        onNew(type) {
            switch (type) {
                case 'person':
                case 'organization':
                    this.$router.push({ name: 'address-edit', params: { id: 'new' }, query: { legal_type: type } });
                    break;
                case 'event_organizer':
                    this.$router.push({
                        name: 'address-edit',
                        params: { id: 'new' },
                        query: { legal_type: 'organization', type: 'event_organizer' },
                    });
                    break;
                case 'location':
                    this.showNewRecordDialog = true;
                    break;
            }
        },

        showAddress(address, location) {
            this.$router.push({
                name: 'address-edit',
                params: { id: address.getId() },
            });
        },

        onEnhancedSearch(searchParams) {
            this.extendedSearchParams = searchParams;
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
            this.showErwSuche = false;
        },

        resetEnhancedSearch() {
            this.extendedSearchParams = {};
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
        },

        async duplicateAddress(address) {
            this.loading = true;
            try {
                await api().post('/address/duplicate', {
                    id: address.id,
                });
                this.$refs.addressList.reload();
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.loading = false;
            }
        },

        async loadAllSelectedItems() {
            const newColl = new AddressCollection();
            const res = await newColl.query({ id: this.selected });
            this.allSelectedItems = res.models;
        },

        async showSoftDeleteDialog() {
            await this.loadAllSelectedItems();
            if (!this.allSelectedItems.length) {
                this.allSelectedItems = [];
            } else {
                this.isSoftDeleteDialogVisible = true;
            }
        },

        hideSoftDeleteDialog() {
            this.isSoftDeleteDialogVisible = false;
        },

        agreeDeletion() {
            this.$refs.addressList.deleteItems(this.allSelectedItems);
            this.removeSelections();
            this.hideSoftDeleteDialog();
        },
    },
};
</script>

<style lang="scss" scoped>
.address-list {
    ::v-deep table > tbody > tr > td {
        vertical-align: top;
        color: var(--gray-text-color);
    }
}
.preview-icons {
    ::v-deep {
        .v-responsive__content {
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;

            a {
                width: 22px;
                height: 22px;
                display: inline-block;
                background-color: var(--tgk-grey-5);
                opacity: 0.4;
                .ic {
                    opacity: 0.4;
                }

                &:hover {
                    opacity: 1;
                    .ic {
                        opacity: 1;
                    }
                }
            }
        }
    }
}
</style>
